import {
  MutationCache,
  QueryCache,
  QueryClient,
} from "@tanstack/react-query";
import {
  ApiError,
  NotFoundError,
  TrilhaLockedError,
  UnauthorizedError,
} from "../api/client";

/**
 * The app's one QueryClient, in a module of its own.
 *
 * It used to live in App.tsx, which was fine until the ROUTER needed it: route
 * guards (router.tsx `beforeLoad`) read cached data to decide a redirect, and
 * App.tsx already imports the router — so leaving it there would have made the
 * two import each other.
 *
 * The 401 handler is registered from App.tsx rather than written here for the
 * same reason: expiring a session ends in `router.navigate`, and this file must
 * not know the router exists. Until it is registered, a 401 is simply an error
 * like any other, which is the right behaviour for the handful of milliseconds
 * before the app mounts.
 */

let onUnauthorized: (() => void) | null = null;

/** Called by App.tsx once, with what to do when a session turns out to be dead. */
export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

function onApiError(error: unknown) {
  if (error instanceof UnauthorizedError && onUnauthorized !== null) {
    onUnauthorized();
  }
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onApiError }),
  mutationCache: new MutationCache({ onError: onApiError }),
  defaultOptions: {
    queries: {
      // Only retry what a second attempt could actually change. A 401, a 404,
      // a locked tópico and a 4xx verdict from the API (a 422 says the same
      // thing every time) will not fix themselves, and retrying them three times
      // only delays the message the student needs to read by several seconds.
      //
      // The lock is the worst of them to retry: it is not a failure at all, so
      // the screen sits on a skeleton through three backoffs before showing an
      // offer the server answered with immediately.
      retry: (failureCount, error) => {
        if (
          error instanceof UnauthorizedError ||
          error instanceof NotFoundError ||
          error instanceof TrilhaLockedError
        ) {
          return false;
        }

        if (error instanceof ApiError && !error.isTransient) {
          return false;
        }

        return failureCount < 3;
      },
    },
  },
});
